import '@awesome.me/webawesome/dist/components/tab/tab.js';
import '@awesome.me/webawesome/dist/components/tab-group/tab-group.js';
import '@awesome.me/webawesome/dist/components/tab-panel/tab-panel.js';
import '@awesome.me/webawesome/dist/styles/webawesome.css';
import '../components/access_level_badge.js';
import '../components/breadcrumb.js';
import '../components/counter_badge.js';
import '../components/empty_state.js';
import '../components/error_state.js';
import '../components/organization_access.js';
import '../components/organization_farm_access.js';
import '../components/organization_farms.js';
import '../components/organization_invitations.js';
import '../components/organization_master_data.js';
import '../components/organization_transfers.js';
import '../components/organization_type_badge.js';
import '../libraries/events.js';

import { faLock } from '@fortawesome/free-solid-svg-icons';
import { Task, TaskStatus } from '@lit/task';
import { html, LitElement, type PropertyValues, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, type Ref, ref } from 'lit/directives/ref.js';

import type {
    OrganizationType,
} from '../libraries/api_client/organization.js';
import type {
    IApiFailure,
} from '../libraries/api_client/request.js';
import type { DetailMode } from '../libraries/detail_mode.js';
import {
    detailPageHeading,
    detailPageStyles,
    detailPageTabs,
    type IDetailPageTab,
} from '../libraries/detail_page.js';
import { setDocumentTitle } from '../libraries/document_title.js';
import { isRecordUnavailable } from '../libraries/http_status.js';
import { redirectTo } from '../libraries/navigation.js';
import { organizationErrorCopy } from '../libraries/organization_copy.js';
import {
    getOrganizationRolePreset,
    hasOrganizationAccess,
    permissionsChangedEventName,
} from '../libraries/permissions.js';
import { resourceErrorHeading, resourceErrorState } from '../libraries/resource_error_state.js';
import { skeletonStyles } from '../libraries/skeletons.js';
import type { ITabLeaf } from '../libraries/tab_leaf.js';
import { resolveEffectiveTab } from '../libraries/tabs.js';
import { visuallyHiddenStyles } from '../libraries/visually_hidden_styles.js';
import { onWindowEvent, WindowEventsController } from '../libraries/window_events.js';

export type OrganizationDetailTab =
    | 'master-data' | 'farms' | 'invitations' | 'organization-access' | 'farm-access' | 'transfers';

const orderedTabs: ReadonlyArray<OrganizationDetailTab> = [
    'master-data',
    'farms',
    'invitations',
    'organization-access',
    'farm-access',
    'transfers',
];

/**
 * Type guard for `OrganizationDetailTab` strings coming off the
 * `wa-tab-show` event. The event's `name` is typed as `string` (it
 * surfaces whatever `panel` attribute the user clicked), so any handler
 * that wants to use it as a discriminator must verify shape first.
 * @param name - The candidate name from the tab event.
 * @returns Whether `name` is one of the declared tab values.
 */
function isOrganizationDetailTab (name: string): name is OrganizationDetailTab {
    return (orderedTabs as ReadonlyArray<string>).includes(name);
}

interface IAccess {
    canRead: boolean;
    canWrite: boolean;
    canManage: boolean;
    rolePreset: string | null;
}

const denyAccess: IAccess = {
    canRead: false,
    canWrite: false,
    canManage: false,
    rolePreset: null,
};

/** The document title of the Stammdaten tab, as its route names it. */
const masterDataTitle = 'Stammdaten der Organisation';

/**
 * Organisations-Detail page. Owns identity (`organizationId`, `tab`),
 * the access snapshot, the tab strip, and the orchestration that drives
 * each leaf's `refresh()`. Page-internal data fetching lives entirely in
 * the leaves; this page never touches the network for organisation /
 * farms / transfers itself. The display name for the breadcrumb +
 * heading is sourced from the master-data leaf via the
 * `fm-display-name-changed` event so the same `GET /organizations/:id`
 * response is not fetched twice.
 *
 * Tab visibility has a single source, `tabVisibility`: the Betriebe tab is
 * conditional on `access.canRead` and the Einladungen / Organisationszugriff
 * / Betriebszugriff / Umzüge tabs on `access.canManage`. `effectiveTab` falls
 * back to `master-data` whenever the tab named by the URL is not visible, so a
 * deep link to a gated tab renders the default tab instead of an empty panel.
 * The URL is deliberately left untouched, and leaf orchestration follows the
 * same effective tab.
 *
 * Full-page error states: when the master-data leaf reports a primary
 * load failure of HTTP 404 or 403 via the `fm-display-name-failed`
 * event payload, the page short-circuits the regular render and
 * surfaces the "Organisation nicht gefunden" / "Kein Zugriff
 * auf die Organisation" `<fm-error-state>` instead. Other failure
 * statuses (transport, 5xx) fall through to the leaf-level callout so
 * the page chrome stays intact.
 */
@customElement('fm-organization-detail')
export class FmOrganizationDetail extends LitElement {

    static styles = [
        detailPageStyles,
        skeletonStyles,
        visuallyHiddenStyles,
    ];

    @property({
        type: String,
    })
    organizationId = '';

    @property({
        type: String,
    })
    tab: OrganizationDetailTab = 'master-data';

    @property({
        type: String,
    })
    mode: DetailMode = 'view';

    @state()
    private displayName: string | null = null;

    /**
     * The organisation's active state, sourced from the master-data leaf
     * via `fm-display-name-changed` and propagated to the sibling leaves
     * so they can disable every mutating CTA while the organisation is
     * deactivated. Defaults `true` (optimistic) so the common active case
     * never flashes disabled CTAs before the leaf reports; the server
     * gates enforce the freeze regardless.
     */
    @state()
    private organizationActive = true;

    @state()
    private organizationType: OrganizationType | null = null;

    @state()
    private pendingInvitationsCount = 0;

    @state()
    private transfersCount = 0;

    @state()
    private permissionsVersion = 0;

    /**
     * Whether the master-data leaf has reported a load failure since
     * the last identity change. Drives the fallback heading
     * ("Organisation" instead of an indefinite skeleton) regardless of
     * failure shape, including transport-level errors that arrive with
     * a `null` failure payload. Cleared when the leaf later reports a
     * fresh display name via `fm-display-name-changed`.
     */
    @state()
    private masterDataLoadFailed = false;

    /**
     * The most recent primary-load failure reported by the master-data
     * leaf via `fm-display-name-failed`. `null` means either no failure
     * has been reported, the last run succeeded, or the failure was a
     * transport-level error with no HTTP status. A non-null value with
     * `status === 404 | 403` promotes the page render to the full-page
     * `<fm-error-state>`; every other status falls through to
     * the leaf-level callout while the parent keeps its chrome.
     */
    @state()
    private organizationLoadFailure: IApiFailure | null = null;

    private lastBootstrappedId: string | null = null;

    private lastActiveTab: OrganizationDetailTab | null = null;

    private bootstrapPending = false;

    private readonly leafRefs: Record<OrganizationDetailTab, Ref<ITabLeaf>> = {
        'master-data': createRef<ITabLeaf>(),
        'farms': createRef<ITabLeaf>(),
        'invitations': createRef<ITabLeaf>(),
        'organization-access': createRef<ITabLeaf>(),
        'farm-access': createRef<ITabLeaf>(),
        'transfers': createRef<ITabLeaf>(),
    };

    /**
     * Whether the master-data leaf's primary load is still in flight, so
     * the tab strip stays hidden behind its skeleton (the leaf is kept
     * mounted to run its fetch, the 403/404 authority). Cleared once the
     * leaf reports a name or a failure, or when the access task errored,
     * because orchestration is skipped there and the leaf never reports.
     * @returns Whether to defer revealing the tab strip.
     */
    private get primaryLoadPending (): boolean {
        return this.displayName === null &&
            !this.masterDataLoadFailed &&
            this.accessTask.status !== TaskStatus.ERROR;
    }

    /**
     * The access snapshot every consumer reads: the tab strip, the panels,
     * `tabVisibility`, and through it the effective tab and leaf
     * orchestration. Falls back to `denyAccess` while the Task has not
     * produced a value yet and after it errored, and keeps the previous value
     * during a re-run so a permissions bump does not collapse the tab strip
     * mid-flight.
     * @returns The access snapshot to gate on.
     */
    private get currentAccess (): IAccess {
        if (this.accessTask.status === TaskStatus.ERROR) {
            return denyAccess;
        }

        return this.accessTask.value ?? denyAccess;
    }

    /**
     * The single source of tab visibility. The tab strip, the panel bodies,
     * and `effectiveTab` all read this.
     * @returns Visibility per tab.
     */
    private get tabVisibility (): Record<OrganizationDetailTab, boolean> {
        const access = this.currentAccess;

        return {
            'master-data': true,
            'farms': access.canRead,
            'invitations': access.canManage,
            'organization-access': access.canManage,
            'farm-access': access.canManage,
            'transfers': access.canManage,
        };
    }

    /**
     * The tab actually rendered, which is the tab named by the URL as long as
     * it is visible and `master-data` otherwise. The URL is not rewritten, so
     * a link to a tab the user regains access to keeps working.
     * @returns The effective tab.
     */
    private get effectiveTab (): OrganizationDetailTab {
        return resolveEffectiveTab(this.tab, this.tabVisibility, 'master-data');
    }

    private readonly accessTask = new Task(this, {
        task: async ([
            organizationId,
        ]): Promise<IAccess> => {
            if (organizationId === '') {
                return denyAccess;
            }

            const [
                canRead,
                canWrite,
                canManage,
                rolePreset,
            ] = await Promise.all([
                hasOrganizationAccess(organizationId, 'organization:read'),
                hasOrganizationAccess(organizationId, 'organization:write'),
                hasOrganizationAccess(organizationId, 'organization:manage'),
                getOrganizationRolePreset(organizationId),
            ]);

            return {
                canRead,
                canWrite,
                canManage,
                rolePreset,
            };
        },
        args: (): readonly [string, number] => [
            this.organizationId,
            this.permissionsVersion,
        ],
        autoRun: true,
    });

    private readonly windowEvents = new WindowEventsController(this, [
        onWindowEvent(permissionsChangedEventName, () => {
            this.permissionsVersion += 1;
        }),
    ]);

    private readonly handleDisplayNameChanged = (
        event: HTMLElementEventMap['fm-display-name-changed']
    ): void => {

        // Discriminate by entityId so a stale dispatch from an earlier
        // mount of the leaf (mid-navigation between organisations)
        // cannot overwrite the new mount's display name.
        if (event.detail.entityId !== this.organizationId) {
            return;
        }

        this.displayName = event.detail.name;
        this.organizationActive = event.detail.active;
        this.organizationType = event.detail.type ?? null;
        this.masterDataLoadFailed = false;
        this.organizationLoadFailure = null;
    };

    private readonly handleDisplayNameFailed = (
        event: HTMLElementEventMap['fm-display-name-failed']
    ): void => {

        // Same stale-mount guard as `handleDisplayNameChanged`: ignore
        // dispatches from a previous mount of the master-data leaf so a
        // late error from organisation A cannot promote the failed-load
        // state on the page for organisation B.
        if (event.detail.entityId !== this.organizationId) {
            return;
        }

        this.masterDataLoadFailed = true;
        this.organizationLoadFailure = event.detail.failure;

        const { failure } = event.detail;

        if (failure !== null && isRecordUnavailable(failure.status)) {
            setDocumentTitle(resourceErrorHeading(failure, organizationErrorCopy));
        }
    };

    private readonly handleInvitationsCountChanged = (
        event: HTMLElementEventMap['fm-count-changed']
    ): void => {
        this.pendingInvitationsCount = event.detail.count;
    };

    private readonly handleTransfersCountChanged = (
        event: HTMLElementEventMap['fm-count-changed']
    ): void => {
        this.transfersCount = event.detail.count;
    };

    override willUpdate (changedProperties: PropertyValues<this>): void {
        if (changedProperties.has('organizationId') &&
            this.organizationId !== this.lastBootstrappedId) {
            this.lastBootstrappedId = this.organizationId;
            this.lastActiveTab = null;
            this.displayName = null;
            this.organizationType = null;
            this.masterDataLoadFailed = false;
            this.organizationLoadFailure = null;
            this.bootstrapPending = true;
        }
    }

    /**
     * Drives leaf orchestration once the access snapshot is known. Lit
     * calls `willUpdate` before reactive-controller `hostUpdate`, so the
     * `accessTask` is still in `INITIAL` status when an identity change
     * is observed (`@lit/task`'s `taskComplete` getter resolves with
     * `undefined` for that status and cannot be awaited reliably).
     * Running orchestration from `updated` instead guarantees we observe
     * the render that materialised the visibility-gated leaves, so
     * `leafRefs` are populated for every tab that should be refreshed.
     *
     * The same hook refreshes the newly active leaf whenever the effective
     * tab changes, which covers both a route change and a tab whose
     * visibility only resolves once the access snapshot lands.
     */
    override updated (): void {
        if (this.bootstrapPending) {
            this.bootstrapOnceAccessSettled();

            return;
        }

        if (this.effectiveTab !== this.lastActiveTab) {
            this.lastActiveTab = this.effectiveTab;
            void this.leafRefs[this.effectiveTab].value?.refresh();
            this.nameFallbackTab();
        }
    }

    /** Titles the document after the Stammdaten when the address names a tab this caller may not see. */
    private nameFallbackTab (): void {
        if (this.effectiveTab !== this.tab && this.effectiveTab === 'master-data') {
            setDocumentTitle(masterDataTitle);
        }
    }

    private bootstrapOnceAccessSettled (): void {
        const { status } = this.accessTask;

        if (status !== TaskStatus.COMPLETE && status !== TaskStatus.ERROR) {
            return;
        }

        this.bootstrapPending = false;
        this.lastActiveTab = this.effectiveTab;
        this.nameFallbackTab();

        if (status === TaskStatus.ERROR) {
            return;
        }

        void this.runLeafOrchestration();
    }

    private async runLeafOrchestration (): Promise<void> {
        const activeTab = this.effectiveTab;
        await this.leafRefs[activeTab].value?.refresh();

        setTimeout(() => {
            for (const name of orderedTabs) {
                if (name !== activeTab) {
                    void this.leafRefs[name].value?.refresh().catch(() => undefined);
                }
            }
        }, 0);
    }

    private readonly handleTabShow = (event: CustomEvent<{ name: string }>): void => {
        const { name } = event.detail;

        // Compared against the effective tab, not the URL's: a tab group
        // announces the tab it activates programmatically, and on a fallback
        // deep-link that is the default tab, which must not rewrite the URL
        // the user opened.
        if (!isOrganizationDetailTab(name) || name === this.effectiveTab) {
            return;
        }

        redirectTo(this.tabUrl(name));
    };

    private tabUrl (name: OrganizationDetailTab): string {
        if (name === 'master-data') {
            return `/organizations/${this.organizationId}`;
        }

        return `/organizations/${this.organizationId}/${name}`;
    }

    private get headerRolePreset (): string | null {
        return this.accessTask.value?.rolePreset ?? null;
    }

    private renderHeader (): TemplateResult {
        const label = this.displayName ?? 'Organisation';

        return detailPageHeading({
            pending: this.displayName === null && !this.masterDataLoadFailed,
            items: [
                {
                    label,
                },
            ],
            label,
            metadata: html`
                <fm-organization-type-badge .organizationType=${this.organizationType}>
                </fm-organization-type-badge>
                <fm-access-level-badge .rolePreset=${this.headerRolePreset}>
                </fm-access-level-badge>
            `,
        });
    }

    /**
     * The tabs this organisation offers. Betriebe, the two access tabs and Umzüge
     * are withheld by {@link FmOrganizationDetail.tabVisibility}; Stammdaten is
     * always offered, which is why it names no visibility.
     * @returns The tab descriptors.
     */
    private get detailTabs (): ReadonlyArray<IDetailPageTab> {
        const visible = this.tabVisibility;

        return [
            {
                name: 'master-data',
                label: 'Stammdaten',
            },
            {
                name: 'farms',
                label: 'Betriebe',
                visible: visible.farms,
            },
            {
                name: 'invitations',
                label: 'Einladungen',
                count: this.pendingInvitationsCount,
                visible: visible.invitations,
            },
            {
                name: 'organization-access',
                label: 'Organisationszugriff',
                visible: visible['organization-access'],
            },
            {
                name: 'farm-access',
                label: 'Betriebszugriff',
                visible: visible['farm-access'],
            },
            {
                name: 'transfers',
                label: 'Umzüge',
                count: this.transfersCount,
                visible: visible.transfers,
            },
        ];
    }

    private renderTabStrip (access: IAccess): TemplateResult {
        return detailPageTabs({
            tabs: this.detailTabs,
            active: this.effectiveTab,
            pending: this.primaryLoadPending,
            onTabShow: this.handleTabShow,
            panels: this.renderPanels(access),
        });
    }

    private renderPanels (access: IAccess): TemplateResult {
        return html`
            <wa-tab-panel name="master-data">
                <fm-organization-master-data
                        ${ref(this.leafRefs['master-data'])}
                        .organizationId=${this.organizationId}
                        .canWrite=${access.canWrite}
                        .canManage=${access.canManage}
                        .mode=${this.mode}
                        @fm-display-name-changed=${this.handleDisplayNameChanged}
                        @fm-display-name-failed=${this.handleDisplayNameFailed}>
                </fm-organization-master-data>
            </wa-tab-panel>
            <wa-tab-panel name="farms">
                ${access.canRead ? html`
                    <fm-organization-farms
                            ${ref(this.leafRefs.farms)}
                            .organizationId=${this.organizationId}
                            .canWrite=${access.canWrite}
                            .organizationActive=${this.organizationActive}>
                    </fm-organization-farms>
                ` : this.renderNoAccess()}
            </wa-tab-panel>
            <wa-tab-panel name="invitations">
                ${access.canManage ? html`
                    <fm-organization-invitations
                            ${ref(this.leafRefs.invitations)}
                            .organizationId=${this.organizationId}
                            .organizationActive=${this.organizationActive}
                            @fm-count-changed=${this.handleInvitationsCountChanged}>
                    </fm-organization-invitations>
                ` : this.renderNoAccess()}
            </wa-tab-panel>
            <wa-tab-panel name="organization-access">
                ${access.canManage ? html`
                    <fm-organization-access
                            ${ref(this.leafRefs['organization-access'])}
                            .organizationId=${this.organizationId}
                            .organizationActive=${this.organizationActive}>
                    </fm-organization-access>
                ` : this.renderNoAccess()}
            </wa-tab-panel>
            <wa-tab-panel name="farm-access">
                ${access.canManage ? html`
                    <fm-organization-farm-access
                            ${ref(this.leafRefs['farm-access'])}
                            .organizationId=${this.organizationId}
                            .organizationActive=${this.organizationActive}>
                    </fm-organization-farm-access>
                ` : this.renderNoAccess()}
            </wa-tab-panel>
            <wa-tab-panel name="transfers">
                ${access.canManage ? html`
                    <fm-organization-transfers
                            ${ref(this.leafRefs.transfers)}
                            .organizationId=${this.organizationId}
                            .organizationActive=${this.organizationActive}
                            @fm-count-changed=${this.handleTransfersCountChanged}>
                    </fm-organization-transfers>
                ` : this.renderNoAccess()}
            </wa-tab-panel>
        `;
    }

    private renderNoAccess (): TemplateResult {
        return html`
            <fm-empty-state
                    .icon=${faLock}
                    heading="Sie haben keinen Zugriff auf diesen Bereich."
                    description="Wenden Sie sich an die zuständige Person, falls Sie hier Aktionen ausführen müssen.">
            </fm-empty-state>
        `;
    }

    /**
     * The state a Organisation the reader cannot open leaves the page in, or `null` where the
     * failure is neither: the page then renders as usual, its own callout reporting it.
     *
     * The state replaces the page rather than standing in it, the header, the tab strip and
     * every tab body being about a record that could not be named.
     * @returns The state, or `null` where the page still stands.
     */
    private renderResourceErrorState (): TemplateResult | null {
        const failure = this.organizationLoadFailure;

        if (failure === null || !isRecordUnavailable(failure.status)) {
            return null;
        }

        return resourceErrorState(failure, organizationErrorCopy, '/organizations');
    }

    render (): TemplateResult {
        const errorState = this.renderResourceErrorState();

        if (errorState !== null) {
            return errorState;
        }

        return html`
            ${this.renderHeader()}
            ${this.accessTask.render({
                initial: () => this.renderTabStrip(denyAccess),
                pending: () => this.renderTabStrip(this.accessTask.value ?? denyAccess),
                complete: access => this.renderTabStrip(access),
                error: () => this.renderTabStrip(denyAccess),
            })}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fm-organization-detail': FmOrganizationDetail;
    }
}
